import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Sidebar from "../../../component/Navigation/Sidebar/Sidebar";
import styles from "./ProductsListComp.module.css";
import search from "../../../assests/search.svg";
import leftArrow from "../../../assests/leftArrow.svg";
import emotions7 from "../../../assests/emotions7.svg";
import mingcute_eye from "../../../assests/mingcute_eye.svg";
import loading from "../../../assests/loading.gif";

const ProductsListComp = (props) => {
  const { variantId } = useParams();
  const navigate = useNavigate();
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [showEyeIcon, setShowEyeIcon] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showBrandFilter, setShowBrandFilter] = useState(false);
  const [showProductFilter, setShowProductFilter] = useState(false);
  
  // Local state for checkboxes - keeps them visible during API calls
  const [localBrands, setLocalBrands] = useState([]);
  const [localProducts, setLocalProducts] = useState([]);
  
  const productsGridRef = useRef(null);
  const brandFilterRef = useRef(null);
  const productFilterRef = useRef(null);

  // Update local state when props change
  useEffect(() => {
    setLocalBrands(props.selectedBrands || []);
  }, [props.selectedBrands]);

  useEffect(() => {
    setLocalProducts(props.selectedProducts || []);
  }, [props.selectedProducts]);

  const handleVariantDetails = (variantId) => {
    navigate(`/home/configuration/productsvariantdetails/${variantId}`);
  };

  const handleMouseEnter = () => setShowEyeIcon(true);
  const handleMouseLeave = () => setShowEyeIcon(false);

  const displayMRP = (variantMrp) => {
    if (!variantMrp) return "N/A";
    if (variantMrp.minMrp === null || variantMrp.maxMrp === null) return "N/A";
    return variantMrp.minMrp === variantMrp.maxMrp 
      ? variantMrp.minMrp 
      : `${variantMrp.minMrp}-${variantMrp.maxMrp}`;
  };

  // Close dropdowns ONLY when clicking outside - NOT on checkbox click
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside brand filter
      if (brandFilterRef.current && !brandFilterRef.current.contains(event.target)) {
        setShowBrandFilter(false);
      }
      // Check if click is outside product filter
      if (productFilterRef.current && !productFilterRef.current.contains(event.target)) {
        setShowProductFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdowns on ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        setShowBrandFilter(false);
        setShowProductFilter(false);
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, []);

  useEffect(() => {
    if (selectedOption && props.filteredItemList.length > 0) {
      const sortedList = [...props.filteredItemList].sort((a, b) => {
        if (selectedOption === "asc") return a.variantName.localeCompare(b.variantName);
        if (selectedOption === "desc") return b.variantName.localeCompare(a.variantName);
        return 0;
      });
      props.setFilteredItemList(sortedList);
    }
  }, [selectedOption]);

  const toggleBrands = () => setShowAllBrands(!showAllBrands);
  const toggleProducts = () => setShowAllProducts(!showAllProducts);
  const toggleMobileFilters = () => setMobileFiltersOpen(!mobileFiltersOpen);
  
  // Toggle brand filter - only opens/closes on button click
  const toggleBrandFilter = (e) => {
    e.stopPropagation();
    setShowBrandFilter(!showBrandFilter);
    // Close product filter when opening brand filter
    if (showProductFilter) setShowProductFilter(false);
  };
  
  // Toggle product filter - only opens/closes on button click
  const toggleProductFilter = (e) => {
    e.stopPropagation();
    setShowProductFilter(!showProductFilter);
    // Close brand filter when opening product filter
    if (showBrandFilter) setShowBrandFilter(false);
  };

  const selectedBrandsCount = props.selectedBrands?.length || 0;
  const selectedProductsCount = props.selectedProducts?.length || 0;
  const hasActiveFilters = selectedBrandsCount > 0 || selectedProductsCount > 0;

  // Handle checkbox change - Update local state immediately, DO NOT close dropdown
  const handleBrandCheckboxChange = (brandName, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Update local state immediately for UI responsiveness
    const updatedBrands = localBrands.includes(brandName)
      ? localBrands.filter((brand) => brand !== brandName)
      : [...localBrands, brandName];
    
    setLocalBrands(updatedBrands);
    
    // Call parent function for API call
    props.handleBrandCheckboxChange(brandName);
    
    // IMPORTANT: Dropdown stays open - no close function called here
  };

  // Handle product checkbox change - Update local state immediately, DO NOT close dropdown
  const handleProductCheckboxChange = (productName, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Update local state immediately for UI responsiveness
    const updatedProducts = localProducts.includes(productName)
      ? localProducts.filter((product) => product !== productName)
      : [...localProducts, productName];
    
    setLocalProducts(updatedProducts);
    
    // Call parent function for API call
    props.handleProductCheckboxChange(productName);
    
    // IMPORTANT: Dropdown stays open - no close function called here
  };

  return (
    <div className={styles.productsContainer}>
      <Sidebar />
      
      {/* Only page load loading */}
      {props.loading && (
        <div className={styles.loadingOverlay}>
          <CircularProgress className={styles.loadingSpinner} />
        </div>
      )}

      <div className={styles.productsMainContent}>
        <header className={styles.productsHeader}>
          <div className={styles.headerLeft}>
            <img 
              src={leftArrow} 
              alt="Back" 
              className={styles.backIcon} 
              onClick={props.arrowBackClick} 
            />
            <h1>Product Search</h1>
          </div>
          
          <div className={styles.searchSortContainer}>
            <div className={styles.searchBox}>
              <input
                type="search"
                placeholder="Search by Brand, Product, or Variant"
                value={props.searchQuery}
                onChange={(e) => {
                  props.handleSearchChange(e);
                  props.setInitialSearch(false);
                }}
                onKeyPress={props.handleKeyPress}
              />
              <img 
                src={search} 
                alt="Search" 
                className={styles.searchIcon} 
                onClick={props.handleSearchClick} 
              />
            </div>

            <div className={styles.filterDropdowns}>
              {/* Brand Filter */}
              {props.brandList?.length > 0 && (
                <div className={styles.filterDropdownWrapper} ref={brandFilterRef}>
                  <button 
                    className={`${styles.filterDropdownTrigger} ${showBrandFilter ? styles.active : ''}`}
                    onClick={toggleBrandFilter}
                  >
                    <span>Brands</span>
                    {selectedBrandsCount > 0 && (
                      <span className={styles.filterBadge}>{selectedBrandsCount}</span>
                    )}
                    <span className={styles.filterArrow}>{showBrandFilter ? '▲' : '▼'}</span>
                  </button>
                  {showBrandFilter && (
                    <div className={styles.filterDropdownMenu}>
                      <div className={styles.filterDropdownHeader}>
                        <span>Select Brands</span>
                        {selectedBrandsCount > 0 && (
                          <button 
                            className={styles.filterClearBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLocalBrands([]);
                              props.handleBrandFilterClear && props.handleBrandFilterClear();
                              // Dropdown stays open
                            }}
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                      <div className={styles.filterDropdownItems}>
                        {(showAllBrands ? props.brandList : props.brandList?.slice(0, 5)).map((brand, index) => (
                          <div key={index} className={styles.filterDropdownItem}>
                            <label onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={localBrands?.includes(brand.brandName)}
                                onChange={(e) => handleBrandCheckboxChange(brand.brandName, e)}
                              />
                              <span>{brand.brandName}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                      {props.brandList?.length > 5 && (
                        <button className={styles.filterViewToggle} onClick={(e) => {
                          e.stopPropagation();
                          toggleBrands();
                        }}>
                          {showAllBrands ? "View Less" : `View More (${props.brandList.length - 5})`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Product Filter */}
              {props.productList?.length > 0 && (
                <div className={styles.filterDropdownWrapper} ref={productFilterRef}>
                  <button 
                    className={`${styles.filterDropdownTrigger} ${showProductFilter ? styles.active : ''}`}
                    onClick={toggleProductFilter}
                  >
                    <span>Products</span>
                    {selectedProductsCount > 0 && (
                      <span className={styles.filterBadge}>{selectedProductsCount}</span>
                    )}
                    <span className={styles.filterArrow}>{showProductFilter ? '▲' : '▼'}</span>
                  </button>
                  {showProductFilter && (
                    <div className={styles.filterDropdownMenu}>
                      <div className={styles.filterDropdownHeader}>
                        <span>Select Products</span>
                        {selectedProductsCount > 0 && (
                          <button 
                            className={styles.filterClearBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLocalProducts([]);
                              props.handleProductFilterClear && props.handleProductFilterClear();
                              // Dropdown stays open
                            }}
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                      <div className={styles.filterDropdownItems}>
                        {(showAllProducts ? props.productList : props.productList?.slice(0, 5)).map((product, index) => (
                          <div key={index} className={styles.filterDropdownItem}>
                            <label onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={localProducts?.includes(product.productName)}
                                onChange={(e) => handleProductCheckboxChange(product.productName, e)}
                              />
                              <span>{product.productName}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                      {props.productList?.length > 5 && (
                        <button className={styles.filterViewToggle} onClick={(e) => {
                          e.stopPropagation();
                          toggleProducts();
                        }}>
                          {showAllProducts ? "View Less" : `View More (${props.productList.length - 5})`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {props.filteredItemList?.length > 0 && (
              <div className={styles.sortDropdown}>
                <select
                  value={props.selectedOption}
                  onChange={props.handleSortChange}
                >
                  <option value="">Sort by</option>
                  <option value="asc">A to Z</option>
                  <option value="desc">Z to A</option>
                </select>
              </div>
            )}
          </div>

          <button className={styles.mobileFilterBtn} onClick={toggleMobileFilters}>
            {mobileFiltersOpen ? <CloseIcon /> : <MenuIcon />}
            <span>Filters</span>
          </button>
        </header>

        <main className={styles.productsMain}>
          <div 
            className={styles.productsGridContainer} 
            ref={props.tableContainerRef}
          >
            {/* Active Filters Display */}
            {hasActiveFilters && (
              <div className={styles.activeFiltersBar}>
                <span className={styles.activeFiltersLabel}>Active Filters:</span>
                {selectedBrandsCount > 0 && (
                  <span className={styles.activeFilterTag}>
                    Brands ({selectedBrandsCount})
                    <button 
                      className={styles.activeFilterRemove}
                      onClick={() => {
                        setLocalBrands([]);
                        props.handleBrandFilterClear && props.handleBrandFilterClear();
                      }}
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedProductsCount > 0 && (
                  <span className={styles.activeFilterTag}>
                    Products ({selectedProductsCount})
                    <button 
                      className={styles.activeFilterRemove}
                      onClick={() => {
                        setLocalProducts([]);
                        props.handleProductFilterClear && props.handleProductFilterClear();
                      }}
                    >
                      ×
                    </button>
                  </span>
                )}
                <button 
                  className={styles.clearAllFilters}
                  onClick={() => {
                    setLocalBrands([]);
                    setLocalProducts([]);
                    props.handleClearAllFilters && props.handleClearAllFilters();
                  }}
                >
                  Clear All
                </button>
              </div>
            )}

            {props.initialSearch ? (
              <div className={styles.emptyState}>
                <p>Please search for a product.</p>
              </div>
            ) : props.loadingProduct ? (
              <div className={styles.loadingState}>
                <img src={loading} alt="Loading" />
                <h3>Loading more products...</h3>
              </div>
            ) : props.hasFilteredItems ? (
              <div className={styles.productsGrid}>
                {props.filteredItemList.map((item) => (
                  <div
                    key={item.variantId}
                    className={styles.productCard}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleVariantDetails(item.variantId)}
                  >
                    <div className={styles.productImageWrapper}>
                      {item.variantImages?.[0] && (
                        <img
                          src={`https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/${item.variantImages[0].imageId}`}
                          alt={item.variantName}
                          className={styles.productImage}
                        />
                      )}
                      <img
                        src={mingcute_eye}
                        alt="View details"
                        className={`${styles.productEyeIcon} ${showEyeIcon ? styles.visible : ""}`}
                      />
                    </div>
                    <div className={styles.productInfo}>
                      <h3>{item.brandName || item.brand || 'Unknown'}</h3>
                      <p>{item.productName || item.product || 'Unknown'}</p>
                      <p>{item.variantName}</p>
                      <div className={styles.priceInfo}>
                        <span>
                          M.R.P: <CurrencyRupeeIcon className={styles.rupeeIcon} />
                          {displayMRP(item.variantMrp)}
                        </span>
                        <span className={styles.productId}>ID - {item.variantId}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <img src={emotions7} alt="No products found" />
                <p>No Product Found</p>
              </div>
            )}

            {props.isLoadingMore && !props.noMoreResults && (
              <div className={styles.loadingMore}>
                <CircularProgress size={24} />
                <h3>Loading more products...</h3>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProductsListComp;